/**
 * Performance Monitoring Utility
 * 
 * Tracks and reports application performance metrics for observability.
 * 
 * Features:
 * - Component render time tracking
 * - API latency measurement
 * - Custom performance marks and measures
 * - Integration with Sentry
 * - Performance budgets and alerts
 * 
 * Usage:
 * ```ts
 * import { measureOperation, trackComponentRender, createPerformanceTracker } from '@/lib/performance-monitor';
 * 
 * await measureOperation('api-call', () => fetch('/api/data'));
 * trackComponentRender('Dashboard', startTime);
 * ```
 */

import * as Sentry from '@sentry/react';
import { 
  safeMarkPerformance, 
  safeMeasurePerformance, 
  hasPerformanceMark 
} from '@/utils/performance-helpers';

/**
 * Performance metric types
 */
export type PerformanceMetricType =
  | 'render'
  | 'api'
  | 'navigation'
  | 'interaction'
  | 'custom';

/**
 * Performance metric data
 */
export interface PerformanceMetric {
  /** Metric name/identifier */
  name: string;
  /** Metric type */
  type: PerformanceMetricType;
  /** Duration in milliseconds */
  duration: number;
  /** Timestamp when metric was recorded */
  timestamp: number;
  /** Additional metadata */
  metadata?: Record<string, any>;
}

/**
 * Performance budget configuration
 */
export interface PerformanceBudget {
  /** Budget name */
  name: string;
  /** Maximum allowed duration in milliseconds */
  maxDuration: number;
  /** Metric type this budget applies to */
  type: PerformanceMetricType;
  /** Warning threshold (percentage of maxDuration) */
  warningThreshold?: number;
}

/**
 * Performance tracker options
 */
export interface PerformanceTrackerOptions {
  /** Whether to log metrics to console */
  enableConsoleLogging?: boolean;
  /** Whether to send metrics to Sentry */
  enableSentryReporting?: boolean;
  /** Performance budgets to enforce */
  budgets?: PerformanceBudget[];
  /** Sample rate (0-1) for sending metrics */
  sampleRate?: number;
}

/**
 * Default performance budgets
 */
const DEFAULT_BUDGETS: PerformanceBudget[] = [
  {
    name: 'component-render',
    type: 'render',
    maxDuration: 100, // 100ms for component render
    warningThreshold: 0.8
  },
  {
    name: 'api-call',
    type: 'api',
    maxDuration: 2000, // 2s for API calls
    warningThreshold: 0.7
  },
  {
    name: 'navigation',
    type: 'navigation',
    maxDuration: 1000, // 1s for navigation
    warningThreshold: 0.8
  },
  {
    name: 'interaction',
    type: 'interaction',
    maxDuration: 100, // 100ms for interactions
    warningThreshold: 0.8
  }
];

/**
 * Global performance tracker instance
 */
class PerformanceTracker {
  private metrics: PerformanceMetric[] = [];
  private options: PerformanceTrackerOptions;
  private budgets: Map<PerformanceMetricType, PerformanceBudget>;

  constructor(options: PerformanceTrackerOptions = {}) {
    this.options = {
      enableConsoleLogging: true,
      enableSentryReporting: true,
      budgets: DEFAULT_BUDGETS,
      sampleRate: 0.1, // Sample 10% of metrics by default
      ...options
    };

    this.budgets = new Map();
    (this.options.budgets || []).forEach(budget => {
      this.budgets.set(budget.type, budget);
    });
  }

  /**
   * Record a performance metric
   */
  record(metric: PerformanceMetric): void {
    this.metrics.push(metric);

    // Check against budget
    const budget = this.budgets.get(metric.type);
    if (budget) {
      this.checkBudget(metric, budget);
    }

    // Log to console if enabled
    if (this.options.enableConsoleLogging) {
      this.logMetric(metric);
    }

    // Send to Sentry if enabled and sampled
    if (this.options.enableSentryReporting && this.shouldSample()) {
      this.reportToSentry(metric);
    }

    // Clean up old metrics (keep last 100)
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }
  }

  /**
   * Check metric against performance budget
   */
  private checkBudget(metric: PerformanceMetric, budget: PerformanceBudget): void {
    const warningThreshold = budget.warningThreshold || 0.8;
    const warningDuration = budget.maxDuration * warningThreshold;

    if (metric.duration > budget.maxDuration) {
      console.error(
        `⚠️ Performance budget exceeded: ${metric.name} took ${metric.duration}ms (budget: ${budget.maxDuration}ms)`
      );

      // Report critical budget violations to Sentry
      Sentry.captureMessage(
        `Performance budget exceeded: ${metric.name}`,
        {
          level: 'warning',
          tags: {
            type: metric.type,
            budget: budget.name
          },
          extra: {
            duration: metric.duration,
            maxDuration: budget.maxDuration,
            exceeded_by: metric.duration - budget.maxDuration,
            metadata: metric.metadata
          }
        }
      );
    } else if (metric.duration > warningDuration) {
      console.warn(
        `⚠️ Performance warning: ${metric.name} took ${metric.duration}ms (warning threshold: ${warningDuration}ms)`
      );
    }
  }

  /**
   * Log metric to console
   */
  private logMetric(metric: PerformanceMetric): void {
    const icon = this.getMetricIcon(metric.type);
    const color = this.getMetricColor(metric.duration);
    
    console.log(
      `${icon} %c${metric.name}%c ${metric.duration.toFixed(2)}ms`,
      'font-weight: bold',
      `color: ${color}`,
      metric.metadata
    );
  }

  /**
   * Report metric to Sentry
   */
  private reportToSentry(metric: PerformanceMetric): void {
    Sentry.addBreadcrumb({
      category: 'performance',
      message: `${metric.name}: ${metric.duration}ms`,
      level: 'info',
      data: {
        type: metric.type,
        duration: metric.duration,
        ...metric.metadata
      }
    });
  }

  /**
   * Determine if this metric should be sampled
   */
  private shouldSample(): boolean {
    return Math.random() < (this.options.sampleRate || 0.1);
  }

  /**
   * Get icon for metric type
   */
  private getMetricIcon(type: PerformanceMetricType): string {
    const icons: Record<PerformanceMetricType, string> = {
      render: '🎨',
      api: '🌐',
      navigation: '🧭',
      interaction: '👆',
      custom: '⚡'
    };
    return icons[type] || '📊';
  }

  /**
   * Get color based on duration
   */
  private getMetricColor(duration: number): string {
    if (duration < 50) return '#22c55e'; // green
    if (duration < 100) return '#eab308'; // yellow
    if (duration < 500) return '#f97316'; // orange
    return '#ef4444'; // red
  }

  /**
   * Get all recorded metrics
   */
  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  /**
   * Get metrics by type
   */
  getMetricsByType(type: PerformanceMetricType): PerformanceMetric[] {
    return this.metrics.filter(m => m.type === type);
  }

  /**
   * Get average duration for a metric name
   */
  getAverageDuration(name: string): number {
    const filtered = this.metrics.filter(m => m.name === name);
    if (filtered.length === 0) return 0;
    
    const sum = filtered.reduce((acc, m) => acc + m.duration, 0);
    return sum / filtered.length;
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
  }
}

/**
 * Global performance tracker instance
 */
export const performanceTracker = new PerformanceTracker();

/**
 * Measure an operation and record its performance
 * 
 * @param name - Operation name
 * @param operation - Operation to measure
 * @param type - Metric type (default: 'custom')
 * @param metadata - Additional metadata
 * @returns Operation result
 * 
 * @example
 * ```ts
 * const data = await measureOperation(
 *   'fetch-user-profile',
 *   () => fetch('/api/profile').then(r => r.json()),
 *   'api',
 *   { userId: '123' }
 * );
 * ```
 */
export async function measureOperation<T>(
  name: string,
  operation: () => Promise<T>,
  type: PerformanceMetricType = 'custom',
  metadata?: Record<string, any>
): Promise<T> {
  const startTime = performance.now();
  
  try {
    const result = await operation();
    const duration = performance.now() - startTime;

    performanceTracker.record({
      name,
      type,
      duration,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        success: true
      }
    });

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    performanceTracker.record({
      name,
      type,
      duration,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    });

    throw error;
  }
}

/**
 * Measure synchronous operation
 * 
 * @param name - Operation name
 * @param operation - Operation to measure
 * @param type - Metric type
 * @param metadata - Additional metadata
 * @returns Operation result
 * 
 * @example
 * ```ts
 * const result = measureSync('calculate-total', () => {
 *   return items.reduce((sum, item) => sum + item.price, 0);
 * }, 'custom');
 * ```
 */
export function measureSync<T>(
  name: string,
  operation: () => T,
  type: PerformanceMetricType = 'custom',
  metadata?: Record<string, any>
): T {
  const startTime = performance.now();
  
  try {
    const result = operation();
    const duration = performance.now() - startTime;

    performanceTracker.record({
      name,
      type,
      duration,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        success: true
      }
    });

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    performanceTracker.record({
      name,
      type,
      duration,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    });

    throw error;
  }
}

/**
 * Track React component render time
 * 
 * @param componentName - Component name
 * @param startTime - Render start time from performance.now()
 * @param metadata - Additional metadata
 * 
 * @example
 * ```ts
 * function MyComponent() {
 *   const renderStart = performance.now();
 *   
 *   useEffect(() => {
 *     trackComponentRender('MyComponent', renderStart);
 *   }, []);
 *   
 *   return <div>...</div>;
 * }
 * ```
 */
export function trackComponentRender(
  componentName: string,
  startTime: number,
  metadata?: Record<string, any>
): void {
  const duration = performance.now() - startTime;

  performanceTracker.record({
    name: componentName,
    type: 'render',
    duration,
    timestamp: Date.now(),
    metadata
  });
}

/**
 * Create a performance marker
 * 
 * @param name - Marker name
 * 
 * @example
 * ```ts
 * mark('user-action-start');
 * // ... some operations
 * const duration = measure('user-action', 'user-action-start');
 * ```
 */
export function mark(name: string): void {
  safeMarkPerformance(name);
}

/**
 * Measure time between two markers
 * 
 * @param name - Measure name
 * @param startMark - Start marker name
 * @param endMark - End marker name (optional, uses current time if not provided)
 * @returns Duration in milliseconds
 * 
 * @example
 * ```ts
 * mark('load-start');
 * await loadData();
 * mark('load-end');
 * const duration = measure('data-load', 'load-start', 'load-end');
 * ```
 */
export function measure(
  name: string,
  startMark: string,
  endMark?: string
): number {
  if (typeof performance === 'undefined') {
    return 0;
  }

  // If no end mark provided, create one
  if (!endMark) {
    endMark = `${name}-end`;
    safeMarkPerformance(endMark);
  }

  const measurement = safeMeasurePerformance(name, startMark, endMark);
  if (measurement) {
    return measurement.duration;
  }

  return 0;
}

/**
 * Create a custom performance tracker for a specific feature
 * 
 * @param featureName - Feature name
 * @param options - Tracker options
 * @returns Custom performance tracker
 * 
 * @example
 * ```ts
 * const dashboardTracker = createPerformanceTracker('dashboard', {
 *   budgets: [{
 *     name: 'dashboard-load',
 *     type: 'render',
 *     maxDuration: 500
 *   }]
 * });
 * 
 * await dashboardTracker.measureOperation('load-data', () => fetchData());
 * ```
 */
export function createPerformanceTracker(
  featureName: string,
  options?: PerformanceTrackerOptions
) {
  const tracker = new PerformanceTracker(options);

  return {
    measureOperation: async <T>(
      name: string,
      operation: () => Promise<T>,
      type: PerformanceMetricType = 'custom',
      metadata?: Record<string, any>
    ) => {
      return measureOperation(
        `${featureName}:${name}`,
        operation,
        type,
        metadata
      );
    },

    measureSync: <T>(
      name: string,
      operation: () => T,
      type: PerformanceMetricType = 'custom',
      metadata?: Record<string, any>
    ) => {
      return measureSync(
        `${featureName}:${name}`,
        operation,
        type,
        metadata
      );
    },

    trackRender: (
      componentName: string,
      startTime: number,
      metadata?: Record<string, any>
    ) => {
      trackComponentRender(
        `${featureName}:${componentName}`,
        startTime,
        metadata
      );
    },

    getMetrics: () => tracker.getMetrics(),
    clear: () => tracker.clear()
  };
}

/**
 * React hook for tracking component performance
 * 
 * @param componentName - Component name
 * @returns Performance tracking utilities
 * 
 * @example
 * ```ts
 * function MyComponent() {
 *   const { trackRender, measureOperation } = usePerformanceTracking('MyComponent');
 *   
 *   useEffect(() => {
 *     trackRender();
 *   }, []);
 *   
 *   const handleClick = async () => {
 *     await measureOperation('button-click', () => performAction());
 *   };
 *   
 *   return <button onClick={handleClick}>Click</button>;
 * }
 * ```
 */
export function usePerformanceTracking(componentName: string) {
  const renderStart = performance.now();

  return {
    trackRender: (metadata?: Record<string, any>) => {
      trackComponentRender(componentName, renderStart, metadata);
    },

    measureOperation: async <T>(
      operationName: string,
      operation: () => Promise<T>,
      metadata?: Record<string, any>
    ) => {
      return measureOperation(
        `${componentName}:${operationName}`,
        operation,
        'interaction',
        metadata
      );
    },

    measureSync: <T>(
      operationName: string,
      operation: () => T,
      metadata?: Record<string, any>
    ) => {
      return measureSync(
        `${componentName}:${operationName}`,
        operation,
        'interaction',
        metadata
      );
    }
  };
}
